import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from "@tanstack/react-query";

export const BackButton = ({ 
  text = "← Back", 
  className = "back-link", 
  onClick = null,
  children,
  fallbackPath, // Keep for backwards compatibility but won't use it by default
  ...props 
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleClick = () => {
    if (onClick) {
      console.log('BackButton: Custom onClick provided');
      onClick();
      return;
    }

    // Check browser navigation state
    const historyLength = window.history.length;
    const referrer = document.referrer;
    const currentOrigin = window.location.origin;
    
    // Determine if we came from within the same app
    const hasInternalReferrer = referrer && referrer.includes(currentOrigin);
    
    // Extract the referrer path if it's internal
    let referrerPath = null;
    if (hasInternalReferrer) {
      try {
        const referrerUrl = new URL(referrer);
        referrerPath = referrerUrl.pathname;
      } catch (e) {
        console.warn('BackButton: Could not parse referrer URL:', referrer);
      }
    }
    
    console.log('BackButton Debug:');
    console.log('  window.history.length:', historyLength);
    console.log('  document.referrer:', referrer);
    console.log('  hasInternalReferrer:', hasInternalReferrer);
    console.log('  referrerPath:', referrerPath);
    
    // Enhanced navigation logic

    // 1. Coming from onboarding — prioritize lastActiveLeagueSlug.
    //    (Distinguish from "no referrer at all" — empty referrer is normal for
    //    direct-link / fresh-tab visits and must NOT short-circuit history.)
    if (referrerPath && referrerPath.startsWith('/onboarding')) {
      const lastLeagueSlug = (() => {
        try {
          const storedSlug = localStorage.getItem('lastActiveLeagueSlug');
          if (!storedSlug) return null;
          const userLeagues = queryClient.getQueryData(['user-leagues']) || [];
          const isValidSlug = userLeagues.some(league => league.slug === storedSlug);
          return isValidSlug ? storedSlug : null;
        } catch (e) {
          return null;
        }
      })();

      if (lastLeagueSlug) {
        console.log(`BackButton: Coming from onboarding, navigating to last active league: /league/${lastLeagueSlug}/table`);
        navigate(`/league/${lastLeagueSlug}/table`);
        return;
      }
    }

    // 2. Primary: browser history for normal in-app navigation.
    if (historyLength > 1) {
      console.log('BackButton: Browser history available, navigating back (-1)');
      navigate(-1);
      return;
    }

    // 3. Try lastActiveLeagueSlug as fallback for other cases
    const lastLeagueSlug = (() => {
      try {
        const storedSlug = localStorage.getItem('lastActiveLeagueSlug');
        if (!storedSlug) return null;
        
        // Validate against cached user leagues (no network call)
        const userLeagues = queryClient.getQueryData(['user-leagues']) || [];
        const isValidSlug = userLeagues.some(league => league.slug === storedSlug);
        
        return isValidSlug ? storedSlug : null;
      } catch (e) {
        return null;
      }
    })();

    if (lastLeagueSlug) {
      console.log(`BackButton: Fallback to last active league slug: /league/${lastLeagueSlug}/table`);
      navigate(`/league/${lastLeagueSlug}/table`);
      return;
    }

    // 4. Fallback to referrer if internal and known (covers non-onboarding cases)
    if (hasInternalReferrer && referrerPath) {
      console.log(`BackButton: Navigating to internal referrer path: ${referrerPath}`);
      navigate(referrerPath);
      return;
    }

    // 5. Explicit fallbackPath prop
    if (fallbackPath) {
      console.log(`BackButton: Navigating to fallbackPath: ${fallbackPath}`);
      navigate(fallbackPath);
      return;
    }

    // 6. No safe destination
    console.log('BackButton: Cannot determine destination - no action taken');
  };

  return (
    <button 
      onClick={handleClick} 
      className={className}
      {...props}
    >
      {children || text}
    </button>
  );
}; 